import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, CalendarCheck, Star, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Particles } from "../components/Particles";
import { Counter } from "../components/Counter";
import { Reveal, SectionHead } from "../components/Reveal";
import { STATS, WHY_US, PROCESS_STEPS, TESTIMONIALS, FAQS, SERVICES } from "../data/content";
import { SERVICE_IMGS, WHY_IMGS } from "../data/images";

const ROTATING = ["Enterprise Software Development", "AI Solutions", "Cloud Services", "Web Development", "Mobile App Development"];

const Hero = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ROTATING.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <section data-testid="hero-section" className="dark relative min-h-screen flex items-center overflow-hidden pt-24 pb-16 bg-background text-foreground">
      <div className="absolute inset-0 grid-pattern" aria-hidden="true" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-indigo-600/25 blur-[130px]" aria-hidden="true" />
      <div className="absolute top-1/3 -right-32 w-[420px] h-[420px] rounded-full bg-cyan-500/20 blur-[130px]" aria-hidden="true" />
      <Particles />
      <motion.div aria-hidden="true" className="absolute top-28 right-[12%] w-16 h-16 border border-primary/30 rounded-2xl hidden md:block"
        animate={{ y: [0, -16, 0], rotate: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }} />
      <motion.div aria-hidden="true" className="absolute bottom-28 left-[6%] w-10 h-10 border border-cyan-400/30 rotate-45 hidden md:block"
        animate={{ y: [0, 14, 0], rotate: [45, 60, 45] }} transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }} />

      <div className="container-x relative max-w-3xl mx-auto text-center">
        <div>
          <motion.span initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-xs font-mono font-medium uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> Premium IT Solutions · Pune, India
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.15] tracking-tight">
            Transforming Businesses{" "}
            <span className="text-gradient inline-block pb-2 -mb-2">Through Technology</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-5 h-8 text-lg sm:text-xl font-medium text-muted-foreground">
            <AnimatePresence mode="wait">
              <motion.span key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35 }} className="inline-flex items-center gap-2" data-testid="hero-rotating-service">
                <CheckCircle2 size={18} className="text-primary" /> {ROTATING[i]}
              </motion.span>
            </AnimatePresence>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-5 max-w-xl mx-auto text-base sm:text-lg text-muted-foreground leading-relaxed">
            VAELIX TECHNOLOGY engineers enterprise-grade software, AI platforms and cloud infrastructure from Pune for businesses that refuse to settle for average.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/services" data-testid="hero-cta-get-started"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_8px_30px_rgba(99,102,241,0.45)] hover:shadow-[0_12px_40px_rgba(99,102,241,0.6)] hover:-translate-y-0.5 transition-all duration-200">
              Get Started <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="hero-cta-consultation"
              className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/5 backdrop-blur-md px-7 py-3.5 text-sm font-semibold text-foreground hover:bg-primary/15 hover:-translate-y-0.5 transition-all duration-200">
              <CalendarCheck size={16} className="text-primary" /> Book Consultation
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
            className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
            {["100+ Projects", "99% Satisfaction", "24/7 Support"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 size={15} className="text-cyan-400" /> {t}</span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Stats = () => (
  <section data-testid="stats-section" className="section-pad !py-14 border-y border-border/60 bg-secondary/40">
    <div className="container-x grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
      {STATS.map((s, idx) => (
        <Reveal key={s.label} delay={idx * 0.08} className="text-center">
          <p className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gradient" data-testid={`stat-value-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <Counter value={s.value} suffix={s.suffix} />
          </p>
          <p className="mt-2 text-sm text-muted-foreground font-medium">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-choose-us-section" className="section-pad">
    <div className="container-x">
      <SectionHead badge="Why VAELIX" title="Why Choose VAELIX TECHNOLOGY"
        sub="The reasons global businesses trust us with their most critical systems." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {WHY_US.map((w, i) => {
          const Icon = Icons[w.icon] || Icons.Sparkles;
          return (
            <motion.div key={w.title} data-testid={`why-us-card-${i}`}
              initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 5) * 0.07 }} whileHover={{ y: -6 }}
              className="glass-card rounded-2xl overflow-hidden">
              <img src={WHY_IMGS[i]} alt={w.title} className="w-full h-24 object-cover" />
              <div className="p-5">
              <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/20 to-cyan-400/20 border border-primary/20 flex items-center justify-center text-primary">
                <Icon size={20} />
              </span>
              <h3 className="mt-4 font-semibold text-sm">{w.title}</h3>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="section-pad bg-secondary/40 border-y border-border/60 overflow-hidden">
    <div className="container-x">
      <SectionHead badge="How We Work" title="Our Development Process"
        sub="A proven 7-step journey from first conversation to long-term partnership." />
      <div className="relative">
        <div className="hidden lg:block absolute top-6 left-[7%] right-[7%] h-px bg-gradient-to-r from-indigo-500/50 via-cyan-400/50 to-indigo-500/50" aria-hidden="true" />
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-7 lg:gap-4">
          {PROCESS_STEPS.map((p, i) => (
            <motion.div key={p.step} data-testid={`process-step-${i + 1}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.09 }} className="relative text-center lg:text-left">
              <span className="relative z-10 mx-auto lg:mx-0 w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 text-white font-mono text-sm font-semibold flex items-center justify-center shadow-[0_6px_20px_rgba(99,102,241,0.45)]">
                {p.step}
              </span>
              <h3 className="mt-4 font-display font-semibold text-base">{p.title}</h3>
              <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials-section" className="section-pad">
    <div className="container-x">
      <SectionHead badge="Testimonials" title="Loved by Founders & CTOs"
        sub="Real words from real clients who shipped real products with us." />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <motion.figure key={t.name} data-testid={`testimonial-card-${i}`}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.1 }} whileHover={{ y: -6 }}
            className={`glass-card rounded-2xl p-6 flex flex-col ${i === 0 ? "lg:row-span-1" : ""}`}>
            <div className="flex gap-1" aria-label={`${t.rating} star rating`}>
              {Array.from({ length: t.rating }).map((_, s) => (
                <Star key={s} size={15} className="text-amber-400 fill-amber-400" />
              ))}
            </div>
            <blockquote className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1">“{t.text}”</blockquote>
            <figcaption className="mt-5 flex items-center gap-3 pt-4 border-t border-border/60">
              <span className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-400 text-white text-xs font-bold flex items-center justify-center">
                {t.initials}
              </span>
              <span className="block text-sm font-semibold">{t.name}</span>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section data-testid="faq-section" className="section-pad bg-secondary/40 border-y border-border/60">
    <div className="container-x max-w-3xl">
      <SectionHead badge="FAQ" title="Frequently Asked Questions" />
      <Reveal>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}
              className="glass-card rounded-xl px-5 border border-border/60 data-[state=open]:border-l-2 data-[state=open]:border-l-primary">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-sm sm:text-base font-semibold hover:no-underline py-4">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const Cta = () => (
  <section data-testid="cta-section" className="section-pad">
    <div className="container-x">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900 via-blue-950 to-slate-950 dark:from-indigo-950 dark:via-blue-950 dark:to-slate-950 border border-indigo-500/30 px-8 py-14 sm:px-14 text-center">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-indigo-500/25 blur-[110px]" aria-hidden="true" />
          <h2 className="relative text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">Ready to transform your business?</h2>
          <p className="relative mt-4 text-slate-300 max-w-xl mx-auto">Book a free consultation and get a transparent, fixed-scope proposal within 48 hours.</p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-book-consultation-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white text-indigo-950 px-7 py-3.5 text-sm font-bold hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(255,255,255,0.25)] transition-all duration-200">
              Book Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/services" data-testid="cta-explore-services-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-7 py-3.5 text-sm font-semibold hover:bg-white/10 transition-all duration-200">
              Explore Services
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="services-preview-section" className="section-pad">
    <div className="container-x">
      <SectionHead badge="What We Do" title="Enterprise Services, End to End"
        sub="Ten practice areas, one accountable partner." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = Icons[s.icon] || Icons.Sparkles;
          return (
            <motion.div key={s.id} data-testid={`home-service-${s.id}`}
              initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 5) * 0.07 }} whileHover={{ y: -6 }}>
              <Link to="/services" className="glass-card rounded-2xl overflow-hidden block h-full group">
                <div className="h-28 overflow-hidden">
                  <img src={SERVICE_IMGS[s.id]} alt={`${s.title} — VAELIX TECHNOLOGY`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="p-5">
                <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 text-white flex items-center justify-center">
                  <Icon size={20} />
                </span>
                <h3 className="mt-4 font-semibold text-sm">{s.title}</h3>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed line-clamp-3">{s.desc}</p>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
        {[
          "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
          "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80",
          "https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=800&q=80",
          "https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&w=800&q=80",
        ].map((src, i) => (
          <Reveal key={src} delay={i * 0.07}>
            <img src={src} alt="VAELIX project work showcase" data-testid={`home-showcase-img-${i}`}
              className="rounded-2xl border border-border/60 shadow-lg w-full h-44 sm:h-56 object-cover hover:scale-[1.03] transition-transform duration-300" />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <Hero />
      <Stats />
      <ServicesPreview />
      <WhyUs />
      <Process />
      <Testimonials />
      <Faq />
      <Cta />
    </main>
  );
}
